import React from 'react';
import {
  ArrowRight,
  CheckCircle,
  XCircle,
} from '@phosphor-icons/react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const formatAddressLines = (addr) => {
  if (!addr) return null;
  const zip = addr.full_zip || addr.zip_code;
  return (
    <>
      {addr.street_address}<br />
      {addr.secondary_address && <>{addr.secondary_address}<br /></>}
      {addr.city}, {addr.state} {zip}
    </>
  );
};

const OriginalAddressBlock = ({ original }) => (
  <div className="bg-slate-50 rounded-lg p-4">
    <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Your Entry</p>
    <p className="text-sm text-slate-700">{formatAddressLines(original)}</p>
  </div>
);

const StandardizedAddressBlock = ({ standardized }) => (
  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
    <div className="flex items-center gap-2 mb-2">
      <CheckCircle size={18} className="text-emerald-600" />
      <p className="text-xs text-emerald-600 uppercase tracking-wide">USPS Verified</p>
    </div>
    <p className="text-sm font-medium text-emerald-800">{formatAddressLines(standardized)}</p>
  </div>
);

const CorrectionsBlock = ({ corrections }) => {
  if (!corrections || corrections.length === 0) return null;
  return (
    <div className="bg-amber-50 rounded-lg p-3">
      <p className="text-xs text-amber-700 font-medium mb-2">Corrections made:</p>
      {corrections.map((c, i) => (
        <p key={`corr-${c.field}-${i}`} className="text-xs text-amber-600">
          {c.field}: "{c.from}" → "{c.to}"
        </p>
      ))}
    </div>
  );
};

const InvalidAddressBlock = ({ error }) => (
  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
    <div className="flex items-center gap-2 mb-2">
      <XCircle size={18} className="text-red-600" />
      <p className="text-sm font-medium text-red-700">Address Not Found</p>
    </div>
    <p className="text-sm text-red-600">
      {error || 'This address could not be verified with USPS.'}
    </p>
    <p className="text-xs text-red-500 mt-2">
      Please check the address and try again, or proceed with the current address.
    </p>
  </div>
);

/**
 * Modal that shows the USPS validation outcome and lets the user accept the
 * standardized version or keep their original entry. Pure presentational.
 */
const AddressValidationModal = ({
  open,
  onOpenChange,
  result,
  onAcceptStandardized,
  onKeepOriginal,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg" data-testid="address-validation-modal">
      <DialogHeader>
        <DialogTitle style={{ fontFamily: 'Manrope' }}>Address Validation</DialogTitle>
        <DialogDescription>
          {result?.is_valid
            ? 'We found a standardized version of your address'
            : 'We could not verify this address'}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-4">
        {result?.is_valid ? (
          <>
            <OriginalAddressBlock original={result.original_address} />
            <div className="flex justify-center">
              <ArrowRight size={24} className="text-indigo-500 rotate-90" />
            </div>
            <StandardizedAddressBlock standardized={result.standardized_address} />
            <CorrectionsBlock corrections={result.corrections} />
          </>
        ) : (
          <InvalidAddressBlock error={result?.error} />
        )}
        {result?.mode === 'mock' && (
          <Badge variant="outline" className="bg-slate-50">
            Demo Mode — Simulated validation
          </Badge>
        )}
      </div>

      <DialogFooter>
        {result?.is_valid ? (
          <div className="flex justify-between w-full">
            <Button variant="outline" onClick={onKeepOriginal}>Keep Original</Button>
            <Button
              onClick={onAcceptStandardized}
              className="bg-indigo-900 hover:bg-indigo-800"
              data-testid="accept-standardized-btn"
            >
              <CheckCircle size={16} className="mr-2" />
              Use Verified Address
            </Button>
          </div>
        ) : (
          <div className="flex justify-end w-full gap-2">
            <Button variant="outline" onClick={onKeepOriginal}>Keep Current Address</Button>
          </div>
        )}
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export default AddressValidationModal;
